/**
 * UI label constants. Each leaf is `{ patient, self }`:
 *   - `patient` — third-person copy (navigator/referrer)
 *   - `self`    — first-person copy (self-booking)
 *
 * Use `resolvePageLabels(page, role)` from `@src/utils/page-labels-resolver`
 * to flatten to a `Record<key, string>` for JSX.
 */

export const PAGE_LABELS = {
  // ── GetStarted form (GetStartedForm.tsx) ─────────────────────────────────
  getStartedForm: {
    // ── Labels ──────────────────────────────────────────────────────────────
    labelZip: {
      patient: "Patient zip code",
      self: "What is your zip code?",
    },
    labelAgeRange: {
      patient: "Patient age range",
      self: "What is your age range?",
    },
    ageRangeGuidance: {
      patient:
        "The selected age range must match the patient's date of birth or appointment booking may be unavailable.",
      self: "The selected age range must match your date of birth or appointment booking may be unavailable.",
    },
    labelPaymentMethod: {
      patient: "How does the patient plan to pay?",
      self: "How do you plan to pay?",
    },
    labelInsurancePolicy: {
      patient: "What is the patient's insurance policy?",
      self: "What is your insurance policy?",
    },

    // ── Aria labels ──────────────────────────────────────────────────────────
    ariaZip: {
      patient: "Enter the patient's zip code",
      self: "Enter your zip code",
    },
    ariaAgeRange: {
      patient: "Select the patient's age range",
      self: "Select your age range",
    },
    ariaPaymentMethod: {
      patient: "Select how the patient plans to pay",
      self: "Select how you plan to pay",
    },
    ariaInsurancePolicy: {
      patient: "Select the patient's insurance policy",
      self: "Select your insurance policy",
    },

    // ── Errors ───────────────────────────────────────────────────────────────
    errorTypeOfService: {
      patient: "Please select the type of service desired for the patient",
      self: "Please select the type of service desired",
    },
    errorCareFormat: {
      patient: "Please select the service type for the patient",
      self: "Please select the service type",
    },
    errorZip: {
      patient: "Please enter the patient's zip code",
      self: "Please enter your zip code",
    },
    errorInvalidZip: {
      patient: "The patient's zip code entered is invalid. Please enter a valid zip code",
      self: "The zipcode entered is invalid. Please enter a valid zip code",
    },
    errorAgeRange: {
      patient: "Please select the patient's age range",
      self: "Please select your age range",
    },
    errorPaymentMethod: {
      patient: "Please select how the patient plans to pay",
      self: "Please select how you plan to pay",
    },
    errorEnrollmentCategory: {
      patient: "Please select an insurance category",
      self: "Please select an insurance category",
    },
    errorInsurancePolicy: {
      patient: "Please enter a valid patient's insurance policy name",
      self: "Please enter a valid insurance policy name",
    },
    errorSessionType: {
      patient: "Please select at least one session type",
      self: "Please select at least one session type",
    },
    errorInsurancePackagesFailed: {
      patient: "Failed to get insurance packages. Please try again",
      self: "Failed to get insurance packages. Please try again",
    },
    errorAddressProcessing: {
      patient: "Unable to process address information",
      self: "Unable to process address information",
    },
  },

  // ── CoverCare form (CoverCareForm.tsx) ────────────────────────────────────
  coverCareForm: {
    // ── Labels ──────────────────────────────────────────────────────────────
    labelPaymentMethod: {
      patient: "How does the patient plan to pay?",
      self: "How do you plan to pay?",
    },
    labelInsurancePolicy: {
      patient: "What is the patient's insurance policy?",
      self: "What is your insurance policy?",
    },
    labelPrimaryPolicyholder: {
      patient: "Is the patient the primary policy holder?",
      self: "Are you the primary policy holder?",
    },
    labelMemberId: {
      patient: "What is the patient's Member ID?",
      self: "What is your Member ID?",
    },
    labelDob: {
      patient: "What is the patient's date of birth?",
      self: "What is your date of birth?",
    },
    labelGender: {
      patient: "What is the patient's gender? (as displayed on the insurance policy)",
      self: "What is your gender? (as displayed on the insurance policy)",
    },

    // ── Links ─────────────────────────────────────────────────────────────────
    linkFindMemberId: {
      patient: "Help me find the patient's Member ID",
      self: "Help me find my Member ID",
    },

    // ── Aria labels ──────────────────────────────────────────────────────────
    ariaPaymentMethod: {
      patient: "Select how the patient plans to pay",
      self: "Select how you plan to pay",
    },
    ariaInsurancePolicy: {
      patient: "Select the patient's insurance policy",
      self: "Select your insurance policy",
    },
    ariaPrimaryPolicyholder: {
      patient: "Select whether the patient is the primary policy holder",
      self: "Select if you are the primary policy holder",
    },
    ariaMemberId: {
      patient: "Enter the patient's Member ID",
      self: "Enter your Member ID",
    },
    ariaDob: {
      patient: "Select the patient's date of birth",
      self: "Select your date of birth",
    },
    ariaGender: {
      patient: "Select the patient's gender",
      self: "Select your gender",
    },

    // ── Errors ───────────────────────────────────────────────────────────────
    errorDOB: {
      patient: "Please enter the patient's date of birth",
      self: "Please enter your date of birth",
    },
    errorPaymentMethod: {
      patient: "Please select how the patient plans to pay",
      self: "Please select how you plan to pay",
    },
    errorPrimaryPolicyholder: {
      patient: "Please identify if the patient is the primary policy holder",
      self: "Please identify if you are the primary policy holder",
    },
    errorGender: {
      patient: "Please select gender as displayed on the patient's policy",
      self: "Please select gender as displayed on your policy",
    },
    errorMemberId: {
      patient: "Please enter a valid patient's Member ID",
      self: "Please enter a valid Member ID",
    },
  },

  // ── Find Member ID modal (FindMemberIDModal.tsx) ──────────────────────────
  findMemberIdModal: {
    headingText: {
      patient: "We’ll help you find the patient’s Member ID",
      self: "We’ll help you find your Member ID",
    },
    bodyText: {
      patient: "In the example, we highlighted where the patient’s member ID should be on the card",
      self: "In the example, we highlighted where your member ID should be on your card",
    },
  },

  // ── PolicyHolder form (PolicyHolderDetailsForm.tsx) ───────────────────────
  policyHolderForm: {
    labelRelationship: {
      patient: "What is the patient's relationship to the policyholder?",
      self: "What is your relationship to the policyholder?",
    },
    ariaRelationship: {
      patient: "Select the patient's relationship to the policyholder",
      self: "Select your relationship to the policyholder",
    },
    errorRelationship: {
      patient: "Please select a valid relationship of the patient to the policyholder",
      self: "Please select a valid relationship",
    },
  },

  // ── Appointment Confirmed (AppointmentConfirmed.tsx) ────────────────
  appointmentConfirmed: {
    headingText: {
      patient: "Patient appointment is confirmed",
      self: "Your appointment is confirmed",
    },
    bodyText: {
      patient:
        "Look for an email from us with important info about the patient's upcoming visit and what to expect next.",
      self: "Look for an email from us with important info about your upcoming visit and what to expect next.",
    },
    followUpText: {
      patient:
        "If we need to follow up about the patient's appointment, a representative will contact you directly.",
      self: "If we need to follow up about your appointment, a representative will contact you directly.",
    },
    supportPhoneText: {
      patient:
        "If you need to add additional services, cancel or reschedule the patient's existing appointment, please call Optum Behavioral Care at {phoneNumber}.",
      self: "If you need to add additional services, cancel or reschedule your existing appointment, please call Optum Behavioral Care at {phoneNumber}.",
    },
  },

  // ── Find Provider (FindProvider/ProviderList/ProviderList.tsx) ───────
  findProvider: {
    noResultsHelpText: {
      patient: "Need help? Call {phoneNumber} to speak with Optum Behavioral Care.",
      self: "Need help? Call {phoneNumber} to speak with Optum Behavioral Care.",
    },
    noResultsAppointmentRequestText: {
      patient:
        "Call {phoneNumber} to finish setting up the patient's appointment, or click below to submit an appointment request",
      self: "Call {phoneNumber} to finish setting up your appointment, or click below to submit an appointment request",
    },
  },

  // ── Patient Screener (PatientScreener.tsx) ───────────────────────────
  patientScreener: {
    headingText: {
      patient: "Medication Management Screening",
      self: "Medication Management Screening",
    },
    bodyText: {
      patient:
        "Please answer the following questions to help us determine if medication management is the right fit for the patient's needs.",
      self: "Please answer the following questions to help us determine if medication management is the right fit for your needs.",
    },
  },

  // ── Cover Care Step (CoverCareStep.tsx) ───────────────────────────────
  coverCareStep: {
    headingText: {
      patient: "How would you like to cover care?",
      self: "How would you like to cover care?",
    },
    bodyText: {
      patient:
        "Whether the patient has insurance or you prefer to pay out of pocket, we’ll help you get started.",
      self: "Whether you have insurance or prefer to pay out of pocket, we’ll help you get started.",
    },
  },

  // ── YourInfo step heading (YourInfoStep.tsx) ──────────────────────────────
  yourInfoStep: {
    stepLabel: {
      patient: "Patient info",
      self: "Your info",
    },
    headingText: {
      patient: "Tell us about the patient",
      self: "Tell us about yourself",
    },
  },

  // ── YourInfo form (YourInfoForm.tsx) ──────────────────────────────────────
  yourInfoForm: {
    // ── Labels ──────────────────────────────────────────────────────────────
    labelDob: {
      patient: "Date of birth",
      self: "What is your date of birth?",
    },
    labelLanguage: {
      patient: "What is the preferred language? (optional)",
      self: "What is your preferred language? (optional)",
    },
    labelEmail: {
      patient: "What is the email?",
      self: "What is your email?",
    },
    labelPhone: {
      patient: "Phone number",
      self: "What is your phone number?",
    },

    // ── Aria labels ──────────────────────────────────────────────────────────
    ariaFirstName: {
      patient: "Enter the patient's first name",
      self: "Enter your first name",
    },
    ariaLastName: {
      patient: "Enter the patient's last name",
      self: "Enter your last name",
    },
    ariaDob: {
      patient: "Enter the patient's date of birth",
      self: "Enter your date of birth",
    },
    ariaLegalSex: {
      patient: "Select the patient's legal sex",
      self: "Select your legal sex",
    },
    ariaGenderIdentity: {
      patient: "Select the patient's gender identity",
      self: "Select your gender identity",
    },
    ariaPreferredPronouns: {
      patient: "Select the patient's preferred pronouns",
      self: "Select your preferred pronouns",
    },
    ariaLanguage: {
      patient: "Select the patient's preferred language",
      self: "Select your preferred language",
    },
    ariaStreetAddress: {
      patient: "Enter the patient's street address",
      self: "Enter your street address",
    },
    ariaStreetAddress2: {
      patient: "Enter the patient's street address line 2",
      self: "Enter your street address line 2",
    },
    ariaCity: { patient: "Enter the patient's city", self: "Enter your city" },
    ariaState: {
      patient: "Enter the patient's state",
      self: "Enter your state",
    },
    ariaZipCode: {
      patient: "Enter the patient's zip code",
      self: "Enter your zip code",
    },
    ariaEmail: {
      patient: "Enter the patient's email address",
      self: "Enter your email address",
    },
    ariaPhone: {
      patient: "Enter the patient's phone number",
      self: "Enter your phone number",
    },
    ariaPhoneType: {
      patient: "Select the patient's phone type",
      self: "Select your phone type",
    },
    ariaContactMethod: {
      patient: "Select the patient's preferred method of contact",
      self: "Select your preferred method of contact",
    },

    // ── Errors ───────────────────────────────────────────────────────────────
    errorFirstName: {
      patient: "Enter patient first name",
      self: "Enter your first name",
    },
    errorLastName: {
      patient: "Enter patient last name",
      self: "Enter your last name",
    },
    errorStreetAddress: {
      patient: "Enter patient street address",
      self: "Enter your street address",
    },
    errorCity: {
      patient: "Enter patient city",
      self: "Enter your city",
    },
    errorState: {
      patient: "Enter patient state",
      self: "Enter your state",
    },
    errorZipCode: {
      patient: "Enter patient zip code",
      self: "Enter your zip code",
    },
    errorEmail: {
      patient: "Enter patient email",
      self: "Enter your email",
    },
    errorPhone: {
      patient: "Enter patient phone number",
      self: "Enter your phone number",
    },
    errorLegalSex: {
      patient: "Select patient legal sex",
      self: "Select your legal sex",
    },
    errorDob: {
      patient: "Enter patient date of birth",
      self: "Enter your date of birth",
    },
    errorPhoneType: {
      patient: "Select patient phone type",
      self: "Select your phone type",
    },
    errorContactMethod: {
      patient: "Select patient preferred contact method",
      self: "Select your preferred contact method",
    },
    errorVoicemailPreference: {
      patient: "Select patient voicemail preference",
      self: "Select your voicemail preference",
    },
    ariaVoicemailPreference: {
      patient: "Select the patient's voicemail preference",
      self: "Select your voicemail preference",
    },
  },
} as const;
